/**
 * API Configuration for HopSeat
 * Handles dynamic backend URL resolution for different environments
 * 
 * IMPORTANT: This is the SINGLE SOURCE OF TRUTH for backend URLs
 * All components should import from here instead of reading env vars directly
 */

// Normalize URL to handle www vs non-www consistently
const normalizeUrl = (url) => {
  if (!url) return url;
  // For hopseat.app, always use non-www version to avoid CORS issues
  return url.replace('https://www.hopseat.app', 'https://hopseat.app');
};

// Get the backend URL from environment or derive from current location
const getBackendUrl = () => {
  // First, check environment variables - must be non-empty
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (envUrl && envUrl.trim() !== '') {
    return normalizeUrl(envUrl);
  }
  
  const backendUrl = process.env.BACKEND_URL;
  if (backendUrl && backendUrl.trim() !== '') {
    return normalizeUrl(backendUrl);
  }
  
  // In production deployment, use the current origin
  // This works because backend and frontend are served from the same domain
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    // For Emergent deployments, the frontend and backend share the same origin
    // Normalize to avoid www vs non-www CORS issues
    return normalizeUrl(window.location.origin);
  }
  
  // Fallback for development
  return 'http://localhost:8001';
};

export const BACKEND_URL = getBackendUrl();
export const API_URL = `${BACKEND_URL}/api`;

// Log for debugging
if (typeof window !== 'undefined') {
  console.log('🔧 API Config - Backend URL:', BACKEND_URL);
}

// Helper function to make API calls with proper error handling
export const apiCall = async (endpoint, options = {}) => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };
  
  // Add authorization header if token exists
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }
  
  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };
  
  const response = await fetch(url, config);
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  
  return response.json();
};

export default {
  BACKEND_URL,
  API_URL,
  apiCall,
};
